import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { login } from "../../shared/_services/auth.services";
import { setChallengeToken } from "../../shared/_helpers/otpChallenge";
import { clearUnconfirmedPaymentNotice, takeUnconfirmedPaymentNotice } from "../../shared/_helpers/paymentErrors";
import MainLogo from "../../assets/images/AstraLogo.svg";

// Exported for tests: which screen a client lands on after login is decided
// entirely here, from fields whose availability the backend has been changing,
// and reaching it through a render would mean standing up Formik, the OTP send
// and the router.
export function getVerifyCodeRouteForUser(user) {
  const userType = user?.userType;

  if (userType === "advisor") {
    return { sessionFrom: "login-advisor", stateFrom: "login-advisor" };
  }

  if (userType === "super-admin" || userType === "admin") {
    return { sessionFrom: "login-admin", stateFrom: "login-admin" };
  }

  if (userType === "client") {
    let route = null;

    if (user?.quickSetup === false) {
      route = { sessionFrom: "login", stateFrom: "login", quickSetup: "false" };
    }
    if (user?.payment_status) {
      route = { sessionFrom: "login", stateFrom: "login" };
    }
    if (!user?.selected_plan) {
      route = { sessionFrom: "no-package", stateFrom: "no-package" };
    }
    // "Had a plan, isn't paying for it" — which is how a lapsed subscription has
    // to be recognised now. This tested `stripe_details.status === "cancelled"`,
    // and backend SEC-058 removed `stripe_details` from every response that emits
    // a user (#53). Nothing public replaces it exactly:
    //   - `selected_plan.status` is a snapshot frozen at purchase ("trialing",
    //     "active"); `utils/subscriptionCron.js` only ever writes "active" back,
    //     so it never reports a cancellation
    //   - `payment_status` is public and is live — the Stripe webhooks set it
    //     false on cancellation, on past_due and on subscription deletion
    //
    // So this is deliberately wider than the test it replaces: a past-due client
    // now takes the same branch a cancelled one does. That is not a regression.
    // Assignment order here means last-match-wins, and with the
    // `stripe_details.status` test unable to match, a cancelled *or* past-due
    // client fell through every branch and this function returned `null` — on
    // which `handleAuthentication` navigates nowhere and the login button simply
    // stops responding. Sending them to /package to re-subscribe is the behaviour
    // that was intended for the cancelled half and is the only route that gets
    // either of them moving again.
    //
    // Astra-Backend#289 asks for a non-sensitive subscription status on the public
    // projection; when that lands, narrow this back to cancelled-only.
    if (user?.selected_plan && !user?.payment_status) {
      route = { sessionFrom: "login-canclledPlan", stateFrom: "login-canclledPlan" };
    }

    return route;
  }

  return null;
}

function persistCurrentUser(data) {
  const payload = data?.data?.data;

  // Backend SEC-114: this response no longer carries a session token — the
  // password step only proves the password, and /otp-verify mints the session.
  // `token` stays in the persisted shape for the guards that read it; it is
  // simply undefined until then. The challenge token that redeems the code goes
  // to sessionStorage rather than into the persisted identity: it is scoped to
  // this tab's flow and is worthless once spent.
  setChallengeToken(payload?.challengeToken);

  const { challengeToken: _challengeToken, ...user } = payload || {};
  const currentUser = { user, token: user?.token };
  window.localStorage.setItem("currentUser", JSON.stringify(currentUser));
  return currentUser;
}

function resolveLoginErrorMessage(data) {
  if (data?.response?.status === 404) {
    return data?.response?.data?.error?.message;
  }
  return data?.response?.data?.message;
}


const Login = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  // A purchase 401 force-logs-out mid-payment; show the contact-support copy
  // that the reload would otherwise have dropped.
  useEffect(() => {
    const notice = takeUnconfirmedPaymentNotice();
    if (notice) {
      enqueueSnackbar(notice, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthentication = async ({ email, password }, setSubmitting) => {
    let lowerCaseEmail = email.toLowerCase();
    const deviceType = "web";
    const data = await login({ email: lowerCaseEmail, password, deviceType });
    if (data.status === 200) {
      clearUnconfirmedPaymentNotice();
      const currentUser = persistCurrentUser(data);
      const verifyRoute = getVerifyCodeRouteForUser(currentUser?.user);

      if (verifyRoute) {
        sessionStorage.setItem("verifyCodeFrom", verifyRoute.sessionFrom);
        navigate("/verify-code", {
          state: {
            from: verifyRoute.stateFrom,
            ...(verifyRoute.quickSetup ? { quickSetup: verifyRoute.quickSetup } : {}),
          },
        });
      }
      return;
    }

    enqueueSnackbar(resolveLoginErrorMessage(data), {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
    });
    setSubmitting(false);
  };

  const initialValues = {
    password: "",
    email: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required.")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    handleAuthentication(values, setSubmitting);
  };

  return (
    <div className="login-signup-wrap login-wrap">
      <div className="container">
        <div className="row">
          <div className="col-md-12 col-lg-7 logo-wrap place-content-center">
            <a className="text-left d-block" href="">
              <img
                className="login-signup-logo"
                src={MainLogo}
                alt="astra logo"
              />
            </a>

            <div className="login-signup-video mobile-hidden-text">
              <div className="onboarding-vdo-login">
                <video src={`${process.env.REACT_APP_BACKEND}/uploads/WelcometoAstraStars.mp4`} controls loop autoPlay muted preload="metadata"></video>
              </div>
              <h2 className="auth-title">Financial Guidance Built Around You</h2>
              <p className="auth-description">Work directly with a dedicated financial advisor who understands your goals — supported by modern tools that keep your plan organized, clear, and always accessible.</p>
              <small className="auth-pointers">• Personal Advisor • Secure Platform • Smart Insights</small>
            </div>

          </div>
          <div className="col-md-12 col-lg-5 place-content-center">
            <div className="login-signup-box signup-box mt-5">
              <h2 className="login-signup-head text-left mt-4 mb-2">Login</h2>
              <p className="login-signup-subhead mb-5">Glad you’re back.!</p>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form>
                    <div className="form-group">
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Email Address"
                        className={`form-control ${errors.email && touched.email ? "is-invalid" : ""
                          }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group password-field mb-3">
                      <Field
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Password"
                        className={`form-control ${errors.password && touched.password ? "is-invalid" : ""
                          }`}
                      />
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="invalid-feedback"
                      />
                      <span
                        className="toggle-password"
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                      >
                        {showPassword ? (
                          <i className="iconoir-eye-closed"></i>
                        ) : (
                          <i className="iconoir-eye"></i>
                        )}
                      </span>
                    </div>
                    <div className="text-right mb-4">
                      <NavLink to="/forgot-password" className=" text-light f-500">
                        Forgot Password?
                      </NavLink>
                    </div>
                    <button
                      type="submit"
                      className="auth-screen-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Loggin in..." : "Login"}
                    </button>
                    <p className="text-center text-light mt-4 mobile-hidden-text f-500 mb-5 text-14">
                      Don't have an account?{" "}
                      <NavLink className="text-light" to="/signup">
                        <u>Register here as client</u>
                      </NavLink>
                    </p>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
